import { useContext } from "react"
import { ButtonBase, Popover } from "@mui/material"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import { rootContext } from "../../contexts/rootContext"
import { riverPointsOfInterest, PoiType } from "../../data/riverPointsOfInterest"
import { pointOfInterestIconsByType, pointOfInterestTypeLabels, pointOfInterestTypes } from "../../utilities/buffaloArcGisHelper"

const accentColor = "#009af2"

const countsByType = riverPointsOfInterest.reduce((counts, pointOfInterest) => {
    counts[pointOfInterest.type] = (counts[pointOfInterest.type] ?? 0) + 1
    return counts
}, {} as Partial<Record<PoiType, number>>)

interface PointsOfInterestMenuProps {
    anchorEl: HTMLElement | null
    onClose: () => void
    visibleTypes: PoiType[]
    onChange: (visibleTypes: PoiType[]) => void
}

// Each type is its own row, and the whole row is the tap target so it is easy
// to hit on a phone. Showing only one type is "Hide all" then that row.
export const PointsOfInterestMenu = (props: PointsOfInterestMenuProps) => {
    const context = useContext(rootContext)
    const areAllVisible = props.visibleTypes.length === pointOfInterestTypes.length

    const handleToggleType = (type: PoiType) => {
        const isVisible = props.visibleTypes.includes(type)
        // Rebuilt from pointOfInterestTypes so the saved order never drifts.
        props.onChange(pointOfInterestTypes.filter((other) =>
            other === type ? !isVisible : props.visibleTypes.includes(other)
        ))
    }

    const handleToggleAll = () => {
        props.onChange(areAllVisible ? [] : [...pointOfInterestTypes])
    }

    return (
        <Popover
            open={props.anchorEl !== null}
            anchorEl={props.anchorEl}
            onClose={props.onClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
                paper: {
                    sx: {
                        marginLeft: "10px",
                        backgroundColor: "black",
                        color: "white",
                        borderRadius: "15px",
                        width: "230px",
                        maxWidth: "calc(100vw - 80px)",
                        padding: "6px 0",
                        userSelect: "none"
                    }
                }
            }}
        >
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 6px 4px 14px"
            }}>
                <span style={{ fontWeight: "bold" }}>Points of Interest</span>
                <ButtonBase
                    onClick={handleToggleAll}
                    sx={{
                        color: accentColor,
                        fontSize: "14px",
                        padding: "8px",
                        borderRadius: "8px",
                        cursor: `url(${context.theme.cursor.pointer}), pointer`,
                        "&:hover": { backgroundColor: "#242424" }
                    }}
                >
                    {areAllVisible ? "Hide all" : "Show all"}
                </ButtonBase>
            </div>
            {pointOfInterestTypes.map((type) => {
                const isVisible = props.visibleTypes.includes(type)
                return (
                    <ButtonBase
                        key={type}
                        onClick={() => handleToggleType(type)}
                        role="checkbox"
                        aria-checked={isVisible}
                        sx={{
                            display: "flex",
                            width: "100%",
                            minHeight: "44px",
                            gap: "12px",
                            padding: "0 4px 0 14px",
                            justifyContent: "flex-start",
                            fontSize: "15px",
                            cursor: `url(${context.theme.cursor.pointer}), pointer`,
                            "&:hover": { backgroundColor: "#242424" }
                        }}
                    >
                        <img
                            src={pointOfInterestIconsByType[type]}
                            alt=""
                            height="24px"
                            width="24px"
                            style={{ opacity: isVisible ? 1 : 0.4 }}
                        />
                        <span style={{ flexGrow: 1, textAlign: "left", opacity: isVisible ? 1 : 0.6 }}>
                            {pointOfInterestTypeLabels[type]}
                        </span>
                        <span style={{ color: "#9e9e9e", fontSize: "13px" }}>
                            {countsByType[type] ?? 0}
                        </span>
                        {isVisible ?
                            <CheckBoxIcon sx={{ color: accentColor, margin: "9px" }} />
                            :
                            <CheckBoxOutlineBlankIcon sx={{ color: "#9e9e9e", margin: "9px" }} />
                        }
                    </ButtonBase>
                )
            })}
        </Popover>
    )
}
